import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Dialog — Radix asosida modal/oyna.
 * Faza 2 use-case'lar:
 *   - InviteMemberDialog (forma)
 *   - ConfirmDeactivateDialog (destruktiv tasdiq)
 *   - RemoveMemberDialog (destruktiv tasdiq)
 *
 * Accessibility:
 *   - Radix focus trap, Esc, aria-labelledby/describedby
 *   - DialogTitle MAJBURIY (a11y), DialogDescription tavsiya
 *   - Animatsiya: spring scale + fade (motion-safe)
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // R4: warm overlay (jigar tonli) + backdrop blur
      "fixed inset-0 z-[var(--z-overlay)] bg-[var(--bg-overlay)] backdrop-blur-md",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Hide default close (X) button. Destruktiv konfirm'da false qoldiramiz. */
  hideClose?: boolean;
  /** Modal kengligi: sm 380px / md 480px / lg 640px / xl 800px */
  size?: "sm" | "md" | "lg" | "xl";
}

const sizeMap: Record<NonNullable<DialogContentProps["size"]>, string> = {
  sm: "max-w-[380px]",
  md: "max-w-[480px]",
  lg: "max-w-[640px]",
  xl: "max-w-[800px]",
};

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose = false, size = "md", ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-[var(--z-modal)] w-[92vw] -translate-x-1/2 -translate-y-1/2",
        // R4: warm dark elevated bg, 12px radius, warm shadow-modal
        "rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-subtle)]",
        "shadow-[var(--shadow-modal)]",
        "focus:outline-none",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        sizeMap[size],
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          aria-label="Yopish"
          className={cn(
            "absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md",
            "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]",
            // R4: coral focus ring
            "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/40",
          )}
        >
          <X className="h-4 w-4" aria-hidden />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = "DialogContent";

export const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col gap-1.5 px-6 pt-6 pb-3", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

export const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("px-6 py-3", className)} {...props} />
);
DialogBody.displayName = "DialogBody";

export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex items-center justify-end gap-2 px-6 pb-6 pt-3",
      "border-t border-[var(--border-subtle)]/50",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      // R4: Inter Tight Display heading uslubi
      "text-[18px] leading-[26px] font-semibold tracking-tight text-[var(--text-primary)]",
      "font-[family-name:var(--font-display,'Inter_Tight_Variable',_'Inter_Variable',_system-ui)]",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      "text-[var(--text-sm)] leading-[var(--text-sm-line)] text-[var(--text-secondary)]",
      className,
    )}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";
